/**
 * Which parent organization a brand or manufacturer belongs to — the read
 * cutover from `lookup_parent_organization` to `parent_organizations`.
 *
 * During the migration an entity can say who owns it two ways:
 *
 * - `parentOrgId`, the foreign key, written by the admin pickers from this
 *   release onward; and
 * - `parentOrg`, the legacy free-text string, which every existing document
 *   carries and which older app versions match by exact value.
 *
 * The FK wins when it names a real document. Otherwise the string is matched
 * against every organization's `alternativeNames` — on the normalised key the
 * parent-org tab's counts and Portfolio already use, so all three surfaces agree
 * on what "Swatch Group" means. That fallback is why this needs no backfill: the
 * seed preserved every legacy spelling as an alias.
 *
 * Nothing here guesses. A string two organizations both answer to is reported
 * ambiguous, and a string nobody answers to is reported unmatched; both render
 * amber, exactly as unmatched lookup values did before.
 */
import type { ParentOrganization } from '../../types';
import { parentOrgMatchKey } from './parentOrgSeed';

/** The organizations, indexed once per load for in-memory resolution. */
export interface ParentOrgIndex {
  byId: Map<string, ParentOrganization>;
  /** Normalised key → ids of every org answering to it. Two ids = ambiguous. */
  byKey: Map<string, string[]>;
  /** Display-name order, for pickers and filter chips. */
  sorted: ParentOrganization[];
}

export const EMPTY_PARENT_ORG_INDEX: ParentOrgIndex = {
  byId: new Map(),
  byKey: new Map(),
  sorted: [],
};

/** What every surface shows for an organization: its short name, else its legal one. */
export function orgDisplayName(org: Pick<ParentOrganization, 'id' | 'name' | 'displayName'>): string {
  return org.displayName?.trim() || org.name?.trim() || org.id;
}

/**
 * Index a loaded collection.
 *
 * An org claims its id, name, displayName and every alternative name. Built once
 * per load of ~two dozen documents — never a read per brand row.
 */
export function buildParentOrgIndex(orgs: readonly ParentOrganization[]): ParentOrgIndex {
  const byId = new Map<string, ParentOrganization>();
  const byKey = new Map<string, string[]>();
  for (const org of orgs) {
    byId.set(org.id, org);
    const keys = new Set(
      [org.id, org.name, org.displayName, ...(org.alternativeNames ?? [])]
        .map((v) => parentOrgMatchKey(v ?? ''))
        .filter(Boolean),
    );
    for (const key of keys) {
      const ids = byKey.get(key) ?? [];
      if (!ids.includes(org.id)) ids.push(org.id);
      byKey.set(key, ids);
    }
  }
  const sorted = [...orgs].sort((a, b) => orgDisplayName(a).localeCompare(orgDisplayName(b)));
  return { byId, byKey, sorted };
}

export type ParentOrgResolution =
  | { kind: 'none' }
  /** Resolved through the foreign key. */
  | { kind: 'fk'; org: ParentOrganization }
  /** Resolved through the legacy string's alternative-name match. */
  | { kind: 'legacy'; org: ParentOrganization; raw: string }
  /** The legacy string matches more than one organization. */
  | { kind: 'ambiguous'; raw: string; candidates: ParentOrganization[] }
  /** The legacy string matches no organization. */
  | { kind: 'unmatched'; raw: string }
  /** The FK names a document that does not exist, and no string rescues it. */
  | { kind: 'dangling'; id: string };

export interface ParentOrgRef {
  parentOrgId?: string | null;
  parentOrg?: string | null;
}

/** Resolve a legacy string alone. */
export function resolveParentOrgString(raw: string, index: ParentOrgIndex): ParentOrgResolution {
  const trimmed = raw.trim();
  if (!trimmed) return { kind: 'none' };
  const ids = index.byKey.get(parentOrgMatchKey(trimmed)) ?? [];
  const orgs = ids.map((id) => index.byId.get(id)).filter((o): o is ParentOrganization => !!o);
  if (orgs.length === 1) return { kind: 'legacy', org: orgs[0], raw: trimmed };
  if (orgs.length > 1) return { kind: 'ambiguous', raw: trimmed, candidates: orgs };
  return { kind: 'unmatched', raw: trimmed };
}

/**
 * Resolve an entity's owner: FK first, then the legacy string.
 *
 * A dangling FK — the organization was deleted — falls back to the string before
 * giving up, because a dual-written document still carries a spelling that may
 * name a surviving organization.
 */
export function resolveParentOrgRef(ref: ParentOrgRef, index: ParentOrgIndex): ParentOrgResolution {
  const id = ref.parentOrgId?.trim();
  if (id) {
    const org = index.byId.get(id);
    if (org) return { kind: 'fk', org };
  }
  const fromString = resolveParentOrgString(ref.parentOrg ?? '', index);
  if (fromString.kind !== 'none' || !id) return fromString;
  return { kind: 'dangling', id };
}

/** Named for the two call sites the migration doc describes; same rule for both. */
export const resolveParentOrgForBrand = resolveParentOrgRef;
export const resolveParentOrgForManufacturer = resolveParentOrgRef;

/** The organization a resolution settled on, when it settled on one. */
export function resolvedOrg(resolution: ParentOrgResolution): ParentOrganization | null {
  return resolution.kind === 'fk' || resolution.kind === 'legacy' ? resolution.org : null;
}

/** How a resolution renders in a listing cell or chip. */
export function resolutionLabel(resolution: ParentOrgResolution): {
  label: string;
  /** False renders amber — the value needs a curator's attention. */
  matched: boolean;
  title: string;
} {
  switch (resolution.kind) {
    case 'none':
      return { label: '', matched: true, title: '' };
    case 'fk':
      return {
        label: orgDisplayName(resolution.org),
        matched: true,
        title: `${orgDisplayName(resolution.org)} (${resolution.org.id})`,
      };
    case 'legacy':
      return {
        label: orgDisplayName(resolution.org),
        matched: true,
        title: `${orgDisplayName(resolution.org)} — matched from “${resolution.raw}”`,
      };
    case 'ambiguous':
      return {
        label: resolution.raw,
        matched: false,
        title: `“${resolution.raw}” matches ${resolution.candidates.length} organizations: ${resolution.candidates
          .map((o) => o.id)
          .join(', ')}`,
      };
    case 'unmatched':
      return {
        label: resolution.raw,
        matched: false,
        title: `“${resolution.raw}” matches no parent organization`,
      };
    case 'dangling':
      return {
        label: resolution.id,
        matched: false,
        title: `parentOrgId “${resolution.id}” names an organization that no longer exists`,
      };
  }
}

/**
 * Filter-chip identity for a resolution: one chip per organization (so
 * `Rolex SA` and `rolex_sa` fold together), and one per unresolved raw value.
 */
export const UNSET_PARENT_ORG_FILTER = 'Unset';
export function parentOrgFilterKey(resolution: ParentOrgResolution): string {
  switch (resolution.kind) {
    case 'none':
      return UNSET_PARENT_ORG_FILTER;
    case 'fk':
    case 'legacy':
      return `org:${resolution.org.id}`;
    case 'ambiguous':
    case 'unmatched':
      return `raw:${resolution.raw}`;
    case 'dangling':
      return `id:${resolution.id}`;
  }
}

/**
 * The legacy string to dual-write beside `parentOrgId`: the organization's
 * display name (falling back to its name).
 *
 * Alternative names are for MATCHING — recognising an old or foreign spelling
 * as this organization. They are not for display or grouping, and this string
 * is what anything still reading the legacy field shows and groups by. It used
 * to be the entity's current spelling when that already belonged to the org,
 * else the org's FIRST alternative name — which for Peacock Watch Group is its
 * Chinese legal name, so a manufacturer linked correctly by id was stored, and
 * then listed, as `孔雀表（集团）有限公司`.
 */
export function legacySpellingFor(org: ParentOrganization): string {
  return orgDisplayName(org);
}

/** The pair a picker selection writes. `null` org clears both. */
export function parentOrgSelection(
  org: ParentOrganization | null,
): { parentOrgId: string; parentOrg: string } {
  if (!org) return { parentOrgId: '', parentOrg: '' };
  return { parentOrgId: org.id, parentOrg: legacySpellingFor(org) };
}

/**
 * The pair for a parent org given as TEXT — an AI enrich suggestion.
 *
 * Links by id only when the text names exactly one organization. An ambiguous or
 * unknown name is kept as the legacy string with no FK, which is what applying
 * it did before this cutover, and the picker then shows it amber for a curator
 * to resolve. A model's guess never becomes a foreign key on its own.
 */
export function parentOrgFromText(text: string, index: ParentOrgIndex): {
  parentOrgId: string;
  parentOrg: string;
} {
  const resolution = resolveParentOrgString(text, index);
  if (resolution.kind === 'legacy') return parentOrgSelection(resolution.org);
  return { parentOrgId: '', parentOrg: text.trim() };
}

/** One filter chip on a brand or manufacturer listing. */
export interface ParentOrgChip {
  key: string;
  label: string;
  matched: boolean;
  title: string;
  count: number;
}

/**
 * Filter chips for a listing: one per organization, one per value that resolves
 * to no single organization (amber), and the Unset count kept apart, as the
 * brand listing always has. Busiest first, then by label so ties hold still.
 */
export function parentOrgChips(
  entities: readonly ParentOrgRef[],
  index: ParentOrgIndex,
): { chips: ParentOrgChip[]; unset: number } {
  const byKey = new Map<string, ParentOrgChip>();
  let unset = 0;
  for (const entity of entities) {
    const resolution = resolveParentOrgRef(entity, index);
    if (resolution.kind === 'none') {
      unset += 1;
      continue;
    }
    const key = parentOrgFilterKey(resolution);
    const existing = byKey.get(key);
    if (existing) {
      existing.count += 1;
      continue;
    }
    byKey.set(key, { key, ...resolutionLabel(resolution), count: 1 });
  }
  const chips = [...byKey.values()].sort(
    (a, b) => b.count - a.count || a.label.localeCompare(b.label),
  );
  return { chips, unset };
}

/**
 * Whether an entity passes a chip filter.
 *
 * A key with no `org:` / `raw:` / `id:` prefix is treated as a raw stored string
 * matched exactly — what the brand listing's filter held before this cutover —
 * so a selection made before a deploy still selects something after it.
 */
export function matchesParentOrgFilter(
  entity: ParentOrgRef,
  filterKey: string | null | undefined,
  index: ParentOrgIndex,
): boolean {
  if (!filterKey) return true;
  const resolution = resolveParentOrgRef(entity, index);
  if (/^(org|raw|id):/.test(filterKey) || filterKey === UNSET_PARENT_ORG_FILTER) {
    return parentOrgFilterKey(resolution) === filterKey;
  }
  return (entity.parentOrg ?? '') === filterKey;
}
